import { useState } from 'react';

const SUBMIT_URL = 'https://readdy.ai/api/form/daimk5boh653ivfvoghg';

type FormResponse = {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
};

const TOPICS = ['娱乐区', '社会区', '科技圈', '体育圈', '网红圈', '内容合作', '其他建议'];

export default function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [messageLength, setMessageLength] = useState(0);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      setMessageLength(0);
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') {
        return;
      }
      if (typeof value === 'string' && value.trim() === '') {
        return;
      }
      payload.append(key, String(value));
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = serverMsg.toLowerCase().includes('spam');

      if (!response.ok || isSpam || (parsed?.code && parsed.code !== 'OK')) {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后再试。');
        return;
      }

      setStatus('success');
      setFormError('');
      form.reset();
      setMessageLength(0);
    } catch {
      setStatus('error');
      setFormError('网络似乎出了点问题，请稍后再试。');
    }
  };

  return (
    <form
      data-readdy-form
      id="contact-form-17chigua"
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <h3 className="font-heading text-lg font-bold text-foreground-950">给我们留言</h3>
      <p className="mt-2 text-sm text-foreground-600">
        想看哪个板块的更多内容，或者有话题推荐，都可以通过下面的表单告诉我们。
      </p>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-foreground-700">称呼</span>
          <input
            type="text"
            name="name"
            required
            placeholder="怎么称呼你"
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-foreground-700">邮箱</span>
          <input
            type="email"
            name="email"
            required
            placeholder="you@example.com"
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </label>
      </div>

      <label className="mt-4 flex flex-col gap-1.5">
        <span className="text-sm font-medium text-foreground-700">相关板块</span>
        <select
          name="topic"
          defaultValue={TOPICS[0]}
          className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
        >
          {TOPICS.map((topic) => (
            <option key={topic} value={topic}>
              {topic}
            </option>
          ))}
        </select>
      </label>

      <label className="mt-4 flex flex-col gap-1.5">
        <span className="flex items-center justify-between text-sm font-medium text-foreground-700">
          <span>留言内容</span>
          <span className="text-xs font-normal text-foreground-500">{messageLength}/500</span>
        </span>
        <textarea
          name="message"
          required
          rows={5}
          maxLength={500}
          onChange={(event) => setMessageLength(event.target.value.length)}
          placeholder="想推荐的话题、想看的板块，或者对我们的建议…"
          className="resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-800 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
        />
      </label>

      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="field-verify-8x2q"
      />

      {status === 'error' && formError && (
        <p className="mt-4 rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-sm text-primary-700">
          {formError}
        </p>
      )}
      {status === 'success' && (
        <p className="mt-4 flex items-center gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-3 py-2 text-sm text-secondary-800">
          <i className="ri-checkbox-circle-line" /> 已经收到你的留言，我们会尽快查看。
        </p>
      )}

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="mt-5 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
      >
        {status === 'submitting' ? '提交中…' : '提交留言'}
        <i className="ri-send-plane-line" />
      </button>
    </form>
  );
}